import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { getOrCreateClientAuthCode } from "~/common/client-auth-code"
import { formatMonthlyPrice } from "~/common/format-monthly-price"
import { lettermemeSubscribedPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { useToast } from "~/ui/use-toast"

const subscribeToLettermemeMutation = gql(/* GraphQL */ `
  mutation SubscribeToLettermeme($input: LettermemeSubscriptionCreateInput!) {
    lettermemeSubscriptionCreate(input: $input) {
      lettermemeSubscription {
        id
        lettermeme {
          id
          urlPath
          viewerIsSubscribed
        }
      }
    }
  }
`)

const stripeCheckoutSessionCreateMutation = gql(/* GraphQL */ `
  mutation StripeCheckoutSessionCreate($input: StripeCheckoutSessionCreateInput!) {
    stripeCheckoutSessionCreate(input: $input) {
      url
      checkoutSessionId
    }
  }
`)

const viewerOwnedLettermemesQuery = gql(/* GraphQL */ `
  query ViewerOwnedLettermemesForSubscribeForm {
    viewer {
      id
      ownedLettermemes {
        id
      }
    }
  }
`)

const subscribeFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
})

type SubscribeFormValues = z.infer<typeof subscribeFormSchema>

interface SubscribeFormProps {
  lettermemeId: string
  isSubscribed: boolean
  isPrivate: boolean
  monetizationEnabled: boolean
  subscriptionPriceCents?: number | null
  subscriptionCurrency: string
}

export const SubscribeForm: React.FC<SubscribeFormProps> = ({
  lettermemeId,
  isSubscribed,
  isPrivate,
  monetizationEnabled,
  subscriptionPriceCents,
  subscriptionCurrency,
}) => {
  const { viewer } = useViewerMaybe()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { data: ownedData } = useQuery(viewerOwnedLettermemesQuery, {
    fetchPolicy: "cache-first",
    skip: !viewer,
  })

  const form = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeFormSchema),
    defaultValues: {
      email: "",
    },
  })

  const [subscribe, { loading: subscribeLoading }] = useSafeMutation(subscribeToLettermemeMutation)
  const [createCheckoutSession, { loading: checkoutLoading }] = useSafeMutation(
    stripeCheckoutSessionCreateMutation
  )

  const loading = subscribeLoading || checkoutLoading

  const priceLabel = monetizationEnabled
    ? formatMonthlyPrice(subscriptionPriceCents, subscriptionCurrency)
    : null

  const runSubscribe = async (email?: string) => {
    try {
      const result = await subscribe({
        variables: {
          input: {
            lettermemeId,
            email,
            manualSubscribe: false,
          },
        },
      })

      const urlPath =
        result.data?.lettermemeSubscriptionCreate?.lettermemeSubscription?.lettermeme?.urlPath

      if (urlPath) {
        navigate(lettermemeSubscribedPath({ urlPath }))
      }

      form.reset()
    } catch (e) {
      toast({
        title: "Error",
        description: "Error subscribing to newsletter",
        variant: "destructive",
      })
      console.error(e)
    }
  }

  const runPaidSubscribe = async (email?: string) => {
    try {
      // preserve existing magic-link browser binding behavior for parity with login flow.
      const clientAuthCode = getOrCreateClientAuthCode()
      const result = await createCheckoutSession({
        variables: {
          input: {
            lettermemeId,
            email,
            clientAuthCode,
          },
        },
      })

      const url = result.data?.stripeCheckoutSessionCreate?.url
      if (!url) {
        toast({
          title: "Error",
          description: "Unable to start Stripe checkout",
          variant: "destructive",
        })
        return
      }

      window.location.assign(url)
    } catch (e) {
      toast({
        title: "Error",
        description: "Error starting Stripe checkout",
        variant: "destructive",
      })
      console.error(e)
    }
  }

  const runOwnerSubscribe = async () => {
    if (!viewer?.email) return
    try {
      const result = await subscribe({
        variables: {
          input: {
            lettermemeId,
            email: viewer.email,
            manualSubscribe: true,
          },
        },
      })

      const urlPath =
        result.data?.lettermemeSubscriptionCreate?.lettermemeSubscription?.lettermeme?.urlPath
      if (urlPath) {
        navigate(lettermemeSubscribedPath({ urlPath }))
      }
    } catch (e) {
      toast({
        title: "Error",
        description: "Error subscribing to newsletter",
        variant: "destructive",
      })
      console.error(e)
    }
  }

  const isPaidOnly = Boolean(monetizationEnabled && subscriptionPriceCents)
  const isOwner = Boolean(
    viewer &&
      ownedData?.viewer?.ownedLettermemes?.some(
        (ownedLettermeme) => ownedLettermeme.id === lettermemeId
      )
  )

  if (isSubscribed) return null
  if (isPrivate) return null

  if (viewer) {
    if (isOwner) {
      return (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => runOwnerSubscribe()}
            disabled={loading}
            className="w-full justify-start"
          >
            {/* TODO: Should we not display this to the owner at all? */}
            {loading ? "Subscribing..." : "Subscribe as owner"}
          </Button>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-2">
        {isPaidOnly ? (
          <Button
            type="button"
            onClick={() => runPaidSubscribe()}
            disabled={loading}
            className="w-full justify-start"
          >
            {loading ? "Redirecting..." : `Subscribe for ${priceLabel}/mo`}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => runSubscribe()}
            disabled={loading}
            className="w-full justify-start"
          >
            {loading ? "Subscribing..." : "+ get the daily newsletter"}
          </Button>
        )}
      </div>
    )
  }

  if (isPaidOnly) {
    return (
      <Form {...form}>
        <div className="mb-2 text-sm">Subscribe for {priceLabel}/mo</div>
        <form
          onSubmit={form.handleSubmit((values) => runPaidSubscribe(values.email))}
          className="flex gap-2"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input {...field} type="email" placeholder="Enter email" disabled={loading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Redirecting..." : "Continue"}
          </Button>
        </form>
      </Form>
    )
  }

  return (
    <Form {...form}>
      <div className="mb-2 text-sm">Get the daily newsletter</div>
      <form
        onSubmit={form.handleSubmit((values) => runSubscribe(values.email))}
        className="flex gap-2"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input {...field} type="email" placeholder="Enter email" disabled={loading} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={loading}>
          {loading ? "Subscribing..." : "Subscribe"}
        </Button>
      </form>
    </Form>
  )
}
